import React from "react";
import {
  BarChart, Bar, Cell,
  PieChart, Pie,
  Tooltip, Legend,
  ResponsiveContainer,
} from "recharts";
import { useIntl } from "react-intl";
import styles from "../styles/AdminStats.module.scss";
import { OrderStats } from "../types/adminStats.type";

interface OrdersSectionProps {
  data: OrderStats | null;
}

const COLORS = {
  blue:   "#4f6ef7",
  green:  "#3ecf8e",
  orange: "#f7934f",
  purple: "#7c4ff7",
};

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltip}>
      {label && <div className={styles.tooltipLabel}>{label}</div>}
      {payload.map((p: any) => (
        <div key={p.name} className={styles.tooltipRow}>
          <span className={styles.tooltipDot} style={{ background: p.color || p.fill }} />
          <span className={styles.tooltipName}>{p.name}:</span>
          <span className={styles.tooltipValue}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function OrdersSection({ data }: OrdersSectionProps): React.ReactElement {
  const intl = useIntl();
  const barColors = [COLORS.blue, COLORS.green, COLORS.orange];

  const revenueBarData = data
    ? [
        { name: intl.formatMessage({ id: "admin.stats.orders.total", defaultMessage: "Total" }),   amount: data.totalRevenue   },
        { name: intl.formatMessage({ id: "admin.stats.orders.creator", defaultMessage: "Creator" }), amount: data.creatorRevenue },
        { name: intl.formatMessage({ id: "admin.stats.orders.site", defaultMessage: "Site" }),    amount: data.siteRevenue    },
      ]
    : [];

  const revenuePieData = data
    ? [
        { name: intl.formatMessage({ id: "admin.stats.orders.creatorRevenue", defaultMessage: "Creator Revenue" }), value: data.creatorRevenue, color: COLORS.green  },
        { name: intl.formatMessage({ id: "admin.stats.orders.siteRevenue", defaultMessage: "Site Revenue" }),    value: data.siteRevenue,    color: COLORS.orange },
      ]
    : [];

  return (
    <>
      {/* ── Stat cards ── */}
      <div className={`ui four stackable cards ${styles.cardRow}`}>
        <div className={`ui card ${styles.statCard}`}>
          <div className="content">
            <div className={styles.cardLabel}>{intl.formatMessage({ id: "admin.stats.orders.totalRevenue", defaultMessage: "Total Revenue" })}</div>
            <div className={styles.cardValue} style={{ color: COLORS.blue }}>
              {data ? `$${data.totalRevenue.toLocaleString()}` : "—"}
            </div>
          </div>
        </div>
        <div className={`ui card ${styles.statCard}`}>
          <div className="content">
            <div className={styles.cardLabel}>{intl.formatMessage({ id: "admin.stats.orders.creatorRevenue", defaultMessage: "Creator Revenue" })}</div>
            <div className={styles.cardValue} style={{ color: COLORS.green }}>
              {data ? `$${data.creatorRevenue.toLocaleString()}` : "—"}
            </div>
          </div>
        </div>
        <div className={`ui card ${styles.statCard}`}>
          <div className="content">
            <div className={styles.cardLabel}>{intl.formatMessage({ id: "admin.stats.orders.siteRevenue", defaultMessage: "Site Revenue" })}</div>
            <div className={styles.cardValue} style={{ color: COLORS.orange }}>
              {data ? `$${data.siteRevenue.toLocaleString()}` : "—"}
            </div>
          </div>
        </div>
        <div className={`ui card ${styles.statCard}`}>
          <div className="content">
            <div className={styles.cardLabel}>{intl.formatMessage({ id: "admin.stats.orders.commissionRate", defaultMessage: "Commission Rate" })}</div>
            <div className={styles.cardValue} style={{ color: COLORS.purple }}>
              {data ? `${(data.commissionRate * 100).toFixed(0)}%` : "—"}
            </div>
            <div className={styles.cardSub}>{intl.formatMessage({ id: "admin.stats.orders.platformCut", defaultMessage: "platform cut" })}</div>
          </div>
        </div>
      </div>

      {/* ── Charts ── */}
      <div className={styles.chartsRow}>

        {/* Bar — revenue breakdown */}
        <div className={`ui segment ${styles.chartCard}`}>
          <div className={styles.chartTitle}>{intl.formatMessage({ id: "admin.stats.orders.revenueBreakdown", defaultMessage: "Revenue Breakdown" })}</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={revenueBarData}
              barSize={36}
              margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
            >
              <Tooltip
                content={<ChartTooltip />}
                cursor={{ fill: "rgba(79,110,247,0.06)" }}
              />
              <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                {revenueBarData.map((_, i) => (
                  <Cell key={i} fill={barColors[i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className={styles.barLabels}>
            {revenueBarData.map((d, i) => (
              <div key={d.name} className={styles.barLabel}>
                <span className={styles.barDot} style={{ background: barColors[i] }} />
                {d.name}
              </div>
            ))}
          </div>
        </div>

        {/* Pie — creator vs site split */}
        <div className={`ui segment ${styles.chartCard}`}>
          <div className={styles.chartTitle}>{intl.formatMessage({ id: "admin.stats.orders.revenueSplit", defaultMessage: "Revenue Split" })}</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={revenuePieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={3}
                dataKey="value"
              >
                {revenuePieData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
              <Legend
                iconType="circle"
                iconSize={8}
                formatter={(val) => (
                  <span className={styles.legendLabel}>{val}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* ── Top Orders Table ── */}
      {data?.topOrders?.length ? (
        <div className={`ui segment ${styles.chartCard}`}>
          <div className={styles.chartTitle}>{intl.formatMessage({ id: "admin.stats.orders.topOrders", defaultMessage: "Top Orders" })}</div>
          <table className="ui very basic compact table">
            <thead>
              <tr>
                <th>#</th>
                <th>{intl.formatMessage({ id: "admin.stats.orders.user", defaultMessage: "User" })}</th>
                <th>{intl.formatMessage({ id: "admin.stats.orders.amount", defaultMessage: "Amount" })}</th>
                <th>{intl.formatMessage({ id: "admin.stats.orders.date", defaultMessage: "Date" })}</th>
              </tr>
            </thead>
            <tbody>
              {data.topOrders.map((order, i) => (
                <tr key={i}>
                  <td style={{ color: "#888", fontWeight: 600 }}>{i + 1}</td>
                  <td>
                    <code style={{ fontSize: "0.82rem", color: COLORS.purple }}>
                      {order.name}
                    </code>
                  </td>
                  <td style={{ color: COLORS.green, fontWeight: 600 }}>
                    €{order.totalPrice.toLocaleString()}
                  </td>
                  <td style={{ color: "#aaa", fontSize: "0.85rem" }}>
                    {new Date(order.createdAt).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}